import * as React from 'react';
import {Text as ReactNativeText, TextStyle, StyleProp, TextProps} from 'react-native';
import {mergeAll, flatten} from 'ramda';
import {typography} from 'theme/Typography';
import {colors} from 'theme/Colors';

const BASE: StyleProp<TextStyle> = {
  ...typography.primary,
  color: colors.palette.black,
  fontSize: 14,
};

const variants = {
  /**
   * The default text styles.
   */
  default: BASE,
  /**
   * Header with font family inter
   */
  header: {
    ...BASE,
    ...typography.header,
    fontSize: 32,
  },

  header2: {
    ...BASE,
    ...typography.header,
    fontSize: 24,
  },
  /**
   * for other headers
   */
  inter18: {...BASE, fontSize: 18, ...typography.secondaryHeader},
  inter20: {...BASE, fontSize: 20, ...typography.secondaryHeader},
  inter24: {...BASE, fontSize: 24, ...typography.secondaryHeader},
  /**
   * for paragraph texts
   */
  sourcesans12: {...BASE, fontSize: 12, fontWeight: 400},
  sourcesans14: {...BASE, fontSize: 14, fontWeight: 400},
  sourcesans16: {...BASE, fontSize: 16, fontWeight: 400},
  sourcesans18: {...BASE, fontSize: 18, fontWeight: 400},
  sourcesans20: {...BASE, fontSize: 20, fontWeight: 400},
  //medium variants
  sourcesans16m: {...BASE, fontSize: 16, fontWeight: 500},
  sourcesans18m: {...BASE, fontSize: 18, fontWeight: 500},
  sourcesans20m: {...BASE, fontSize: 20, fontWeight: 500},
  /**
   * Field labels that appear on forms above the inputs.
   */
  fieldLabel: {
    ...BASE,
    fontSize: 18,
    fontWeight: 500,
  },
  /**
   * Placeholder that appears in the inputs.
   */
  placeholder: {
    ...BASE,
    fontSize: 16,
    color: colors.palette.nuetral4Placeholder,
    fontWeight: 500,
  },
  /**
   * Field error
   */
  fieldError: {
    ...BASE,
    fontSize: 12,
    fontWeight: 400,
    color: colors.palette.errorRedDark,
  },
  buttonText: {
    ...BASE,
    fontSize: 18,
    fontWeight: 600,
  },
  breadCrumbsText: {
    ...BASE,
    fontSize: 16,
    fontWeight: 600,
  },
};

/**
 * This component is a HOC over the built-in React Native one.
 */

type propsType = Readonly<{
  variant?: keyof typeof variants;
  text?: string;
  children?: any;
  style?: StyleProp<TextStyle>;
}> & TextProps;

export function Text(props: propsType) {
  const {
    variant = 'default',
    text,
    children,
    style: styleOverride,
    ...rest
  } = props;
  // grab the props

  // figure out which content to use
  const content = text || children;

  /*
    flatten - flattens the array which consist of
  */
  const style = mergeAll(
    flatten([variants[variant] || variants.default, styleOverride]),
  ) as TextStyle;

  return (
    <ReactNativeText {...rest} style={style}>
      {content}
    </ReactNativeText>
  );
}

/**
 * Props
 * children - Children components.
 * text - The text to display
 * style - An optional style override useful for padding & margin.
 * variant - One of the different types of text variants.
 */
